class EmotoFluxTheme {
  constructor() {
    this.body = document.body;
    this.overlay = document.querySelector('[data-overlay]');
    this.bindMenus();
    this.bindNavPanels();
    this.bindSearch();
    this.bindCart();
    this.bindProductForms();
    this.bindCarousels();
    this.bindCountdowns();
    this.bindFilters();
    this.bindRecommendations();
    document.addEventListener('shopify:section:load', (event) => {
      this.bindCarousels(event.target);
      this.bindCountdowns(event.target);
    });
  }

  bindMenus() {
    document.querySelectorAll('[data-menu-open]').forEach((button) => {
      button.addEventListener('click', () => {
        this.menuTrigger = button;
        this.openLayer('menu');
      });
    });
    document.querySelectorAll('[data-menu-close]').forEach((button) => {
      button.addEventListener('click', () => this.closeLayers());
    });
    const megaTrigger = document.querySelector('[data-mega-trigger]');
    const megaPanel = document.querySelector('[data-mega-menu]');
    if (megaTrigger && megaPanel) {
      let megaCloseTimer;
      const openMega = () => {
        clearTimeout(megaCloseTimer);
        document.querySelectorAll('[data-search-panel]').forEach((item) => item.hidden = true);
        megaPanel.hidden = false;
        megaTrigger.setAttribute('aria-expanded', 'true');
      };
      const closeMega = () => {
        megaPanel.hidden = true;
        megaTrigger.setAttribute('aria-expanded', 'false');
      };
      const scheduleMegaClose = () => {
        clearTimeout(megaCloseTimer);
        megaCloseTimer = setTimeout(closeMega, 140);
      };
      megaTrigger.addEventListener('mouseenter', openMega);
      megaTrigger.addEventListener('focus', openMega);
      megaTrigger.addEventListener('mouseleave', scheduleMegaClose);
      megaPanel.addEventListener('mouseenter', () => clearTimeout(megaCloseTimer));
      megaPanel.addEventListener('mouseleave', scheduleMegaClose);
      megaPanel.addEventListener('focusin', openMega);
      megaPanel.addEventListener('focusout', (event) => {
        if (!megaPanel.contains(event.relatedTarget) && event.relatedTarget !== megaTrigger) scheduleMegaClose();
      });
      document.addEventListener('click', (event) => {
        if (!event.target.closest('[data-mega-trigger],[data-mega-menu]')) closeMega();
      });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !megaPanel.hidden) {
          closeMega();
          megaTrigger.focus();
        }
      });
    }
  }

  bindNavPanels() {
    const navItems = document.querySelectorAll('[data-nav-item]');
    if (!navItems.length) return;
    let closeTimer;
    const closeAll = (except) => {
      navItems.forEach((item) => {
        if (item === except) return;
        const panel = item.querySelector('[data-nav-panel]');
        const toggle = item.querySelector('[data-nav-toggle]');
        if (panel) panel.hidden = true;
        if (toggle) toggle.setAttribute('aria-expanded', 'false');
      });
    };
    const openItem = (item) => {
      clearTimeout(closeTimer);
      closeAll(item);
      const panel = item.querySelector('[data-nav-panel]');
      const toggle = item.querySelector('[data-nav-toggle]');
      if (panel) panel.hidden = false;
      if (toggle) toggle.setAttribute('aria-expanded', 'true');
    };
    const scheduleClose = () => {
      clearTimeout(closeTimer);
      closeTimer = setTimeout(() => closeAll(), 140);
    };
    navItems.forEach((item) => {
      const panel = item.querySelector('[data-nav-panel]');
      const toggle = item.querySelector('[data-nav-toggle]');
      if (!panel) return;
      item.addEventListener('mouseenter', () => openItem(item));
      item.addEventListener('mouseleave', scheduleClose);
      item.addEventListener('focusin', () => openItem(item));
      item.addEventListener('focusout', (event) => {
        if (!item.contains(event.relatedTarget)) scheduleClose();
      });
      toggle?.addEventListener('click', () => {
        const isOpen = toggle.getAttribute('aria-expanded') === 'true';
        if (isOpen) {
          panel.hidden = true;
          toggle.setAttribute('aria-expanded', 'false');
        } else {
          openItem(item);
        }
      });
    });
    document.addEventListener('click', (event) => {
      if (!event.target.closest('[data-nav-item]')) closeAll();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const openPanel = document.querySelector('[data-nav-panel]:not([hidden])');
      const item = openPanel?.closest('[data-nav-item]');
      closeAll();
      item?.querySelector('[data-nav-trigger]')?.focus();
    });
  }

  bindSearch() {
    document.querySelectorAll('[data-search-toggle]').forEach((button) => {
      button.addEventListener('click', () => {
        const panel = document.querySelector('[data-search-panel]');
        const open = panel.hasAttribute('hidden');
        document.querySelectorAll('[data-search-panel],[data-mega-menu]').forEach((item) => item.hidden = true);
        panel.hidden = !open;
        if (open) panel.querySelector('input')?.focus();
      });
    });
    document.querySelectorAll('[data-predictive-input]').forEach((input) => {
      let timer;
      input.addEventListener('input', () => {
        clearTimeout(timer);
        timer = setTimeout(() => this.getSuggestions(input), 220);
      });
    });
  }

  async getSuggestions(input) {
    const target = document.querySelector('[data-predictive-results]');
    const query = input.value.trim();
    if (!target || query.length < 2) {
      if (target) target.innerHTML = '';
      return;
    }
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,collection&resources[limit]=6`);
      const payload = await response.json();
      const resources = payload.resources?.results || {};
      const results = [...(resources.products || []), ...(resources.collections || [])];
      target.innerHTML = results.map((item) => `<a href="${item.url}">${this.escape(item.title)}</a>`).join('');
    } catch (error) {
      target.innerHTML = '';
    }
  }

  bindCart() {
    document.querySelectorAll('[data-floating-cart]').forEach((button) => {
      if (button.dataset.cartTriggerBound === 'true') return;
      button.dataset.cartTriggerBound = 'true';
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.openLayer('drawer');
      });
    });
    document.addEventListener('click', (event) => {
      const openButton = event.target.closest('[data-cart-open]');
      if (openButton && document.querySelector('[data-cart-drawer]')) {
        event.preventDefault();
        this.openLayer('drawer');
        return;
      }
      if (event.target.closest('[data-cart-close]')) this.closeLayers();
    });
    this.overlay?.addEventListener('click', () => this.closeLayers());
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.closeLayers();
    });
  }

  bindProductForms() {
    document.querySelectorAll('[data-product-form]').forEach((form) => {
      const variant = form.querySelector('[name="id"]');
      variant?.addEventListener('change', () => this.updateVariant(form, variant));
      const optionSelects = [...form.querySelectorAll('[data-option-select]')];
      const variantJson = form.querySelector('[data-variant-json]');
      if (variant && optionSelects.length && variantJson) {
        let variants = [];
        try {
          variants = JSON.parse(variantJson.textContent);
        } catch (error) {
          variants = [];
        }
        optionSelects.forEach((select, changedIndex) => {
          select.addEventListener('change', () => {
            const selectedValues = optionSelects.map((control) => control.value);
            let match = variants.find((item) => item.options?.every((value, index) => value === selectedValues[index]));
            if (!match) {
              match = variants.find((item) => item.available && item.options?.[changedIndex] === select.value)
                || variants.find((item) => item.options?.[changedIndex] === select.value);
            }
            if (!match) return;
            optionSelects.forEach((control, index) => {
              if (match.options?.[index] != null) control.value = match.options[index];
            });
            variant.value = String(match.id);
            variant.dispatchEvent(new Event('change', { bubbles: true }));
          });
        });
      }
      form.addEventListener('submit', async (event) => {
        if (form.dataset.cartBehavior !== 'drawer') return;
        event.preventDefault();
        const submit = form.querySelector('[type="submit"]');
        submit?.setAttribute('disabled', 'disabled');
        try {
          const response = await fetch('/cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
          if (!response.ok) throw new Error('Unable to add this configuration.');
          await this.refreshCartDrawer();
          this.openLayer('drawer');
        } catch (error) {
          const message = form.querySelector('[data-form-error]');
          if (message) message.textContent = error.message;
        } finally {
          submit?.removeAttribute('disabled');
        }
      });
    });
  }

  updateVariant(form, select) {
    const option = select.options[select.selectedIndex];
    if (!option) return;
    const id = option.value;
    const price = option.dataset.price;
    const compare = option.dataset.compare;
    const image = option.dataset.image;
    const available = option.dataset.available === 'true';
    const sku = option.dataset.sku;
    document.querySelectorAll('[data-current-price]').forEach((node) => { if (price) node.textContent = price; });
    document.querySelectorAll('[data-compare-price]').forEach((node) => {
      node.textContent = compare || '';
      node.hidden = !compare;
    });
    document.querySelectorAll('[data-sticky-variant]').forEach((node) => node.value = id);
    form.querySelectorAll('[data-add-to-cart]').forEach((button) => {
      button.disabled = !available;
      button.setAttribute('aria-disabled', String(!available));
      button.textContent = available ? 'Add to cart' : 'Sold out';
    });
    document.querySelectorAll('.sticky-atc [data-add-to-cart]').forEach((button) => {
      button.disabled = !available;
      button.setAttribute('aria-disabled', String(!available));
      button.textContent = available ? 'Add to cart' : 'Sold out';
    });
    document.querySelectorAll('[data-current-sku]').forEach((node) => {
      node.textContent = sku ? 'SKU: ' + sku : '';
    });
    if (image) {
      const main = document.querySelector('[data-product-main-image]');
      if (main) main.src = image;
    }
    const url = new URL(window.location.href);
    url.searchParams.set('variant', id);
    window.history.replaceState({}, '', url);
  }

  async refreshCartDrawer() {
    const response = await fetch(`${window.location.pathname}?section_id=cart-drawer`);
    const html = await response.text();
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const fresh = parsed.querySelector('[data-cart-drawer]');
    const current = document.querySelector('[data-cart-drawer]');
    if (fresh && current) current.replaceWith(fresh);
    const count = fresh?.dataset.cartItemCount || '0';
    document.querySelectorAll('[data-cart-count]').forEach((node) => { node.textContent = count; });
    document.querySelectorAll('[data-cart-open]').forEach((node) => {
      node.setAttribute('aria-label', `Cart, ${count} ${count === '1' ? 'item' : 'items'}`);
    });
  }

  bindCountdowns(scope = document) {
    scope.querySelectorAll('[data-sale-countdown]').forEach((countdown) => {
      if (countdown.dataset.countdownBound === 'true') return;

      const hours = countdown.querySelector('[data-countdown-hours]');
      const minutes = countdown.querySelector('[data-countdown-minutes]');
      const seconds = countdown.querySelector('[data-countdown-seconds]');
      const timer = countdown.querySelector('[data-countdown-numbers]');
      if (!hours || !minutes || !seconds || !timer) return;

      countdown.dataset.countdownBound = 'true';

      const render = () => {
        const now = new Date();
        const endOfDay = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate(),
          23,
          59,
          59,
          999
        );
        const remaining = Math.max(0, endOfDay.getTime() - now.getTime());
        const hourValue = Math.floor(remaining / 3600000);
        const minuteValue = Math.floor((remaining % 3600000) / 60000);
        const secondValue = Math.floor((remaining % 60000) / 1000);

        hours.textContent = String(hourValue).padStart(2, '0');
        minutes.textContent = String(minuteValue).padStart(2, '0');
        seconds.textContent = String(secondValue).padStart(2, '0');
        timer.setAttribute(
          'aria-label',
          `${hourValue} hours, ${minuteValue} minutes, and ${secondValue} seconds remaining in today's promotion`
        );
      };

      render();
      window.setInterval(render, 1000);
    });
  }

  bindCarousels(scope = document) {
    scope.querySelectorAll('[data-carousel-row]').forEach((row) => {
      if (row.dataset.carouselBound === 'true') return;

      const track = row.querySelector('[data-carousel-track]');
      const prev = row.querySelector('[data-carousel-prev]');
      const next = row.querySelector('[data-carousel-next]');
      const count = row.querySelector('[data-carousel-count]');
      const cards = [...row.querySelectorAll('.ef-sbb__card')];
      if (!track || !cards.length) return;

      row.dataset.carouselBound = 'true';

      const step = () => {
        const styles = window.getComputedStyle(track);
        const gap = parseFloat(styles.columnGap || styles.gap) || 0;
        return cards[0].getBoundingClientRect().width + gap;
      };

      const update = () => {
        const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
        const atStart = track.scrollLeft <= 2;
        const atEnd = track.scrollLeft >= maxScroll - 2;
        if (prev) {
          prev.disabled = atStart;
          prev.setAttribute('aria-disabled', String(atStart));
        }
        if (next) {
          next.disabled = atEnd;
          next.setAttribute('aria-disabled', String(atEnd));
        }
        if (count) {
          const current = Math.min(cards.length, Math.round(track.scrollLeft / step()) + 1);
          count.textContent = `${String(current).padStart(2, '0')} / ${String(cards.length).padStart(2, '0')}`;
        }
      };

      const move = (direction) => {
        track.scrollBy({ left: direction * step(), behavior: 'smooth' });
      };

      prev?.addEventListener('click', () => move(-1));
      next?.addEventListener('click', () => move(1));
      track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  }

  bindFilters() {
    document.querySelectorAll('[data-filter-toggle]').forEach((button) => {
      button.addEventListener('click', () => document.querySelector('[data-filters]')?.classList.toggle('is-open'));
    });
    document.querySelectorAll('[data-sort-select]').forEach((select) => {
      select.addEventListener('change', () => {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', select.value);
        window.location.assign(url);
      });
    });
  }

  bindRecommendations() {
    document.querySelectorAll('product-recommendations[data-url]').forEach(async (element) => {
      if (element.dataset.loaded === 'true') return;
      element.dataset.loaded = 'true';
      try {
        const response = await fetch(element.dataset.url);
        if (!response.ok) return;
        const html = await response.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const fresh = parsed.querySelector('product-recommendations');
        if (fresh?.innerHTML.trim()) element.innerHTML = fresh.innerHTML;
      } catch (error) {
        element.hidden = true;
      }
    });
  }

  openLayer(type) {
    this.closeLayers();
    this.body.classList.add(type === 'menu' ? 'menu-open' : 'drawer-open');
    if (this.overlay) this.overlay.hidden = false;
    if (type === 'menu') {
      const menu = document.querySelector('#MobileMenu');
      this.menuTrigger?.setAttribute('aria-expanded', 'true');
      menu?.setAttribute('aria-hidden', 'false');
      window.setTimeout(() => menu?.querySelector('[data-menu-close]')?.focus(), 80);
    }
  }

  closeLayers() {
    const menuWasOpen = this.body.classList.contains('menu-open');
    this.body.classList.remove('menu-open', 'drawer-open');
    if (this.overlay) this.overlay.hidden = true;
    document.querySelector('#MobileMenu')?.setAttribute('aria-hidden', 'true');
    document.querySelectorAll('[data-search-panel],[data-mega-menu]').forEach((item) => item.hidden = true);
    document.querySelectorAll('[aria-expanded="true"]').forEach((item) => item.setAttribute('aria-expanded', 'false'));
    if (menuWasOpen) this.menuTrigger?.focus();
  }

  escape(value) {
    const node = document.createElement('div');
    node.textContent = value;
    return node.innerHTML;
  }
}

document.addEventListener('DOMContentLoaded', () => new EmotoFluxTheme());

